/**
 * Various functions to compute closest points and parameters on various kinds of geometry.
 * @module
 */
import type { RBush3D } from "rbush-3d";
import { aabb2ContainsVec2, aabb2Overlaps } from "./aabb2";
import { aabb3Overlaps } from "./aabb3";
import type { BRep, Face } from "./brep";
import { faceAABB3 } from "./brep";
import { classifyContainmentVec2PolyLine2Fast } from "./containment";
import { DIST_EPSILON } from "./core";
import { line2Eval } from "./line2";
import { planeEval, planeProject } from "./plane";
import {
  polyLine2AABB2,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2EdgeEnd,
  polyLine2EdgeEval,
  polyLine2EdgeStart,
  polyLine2MergeCoincidentVertices,
  polyLine2MoveVertices,
} from "./polyLine2";
import type { PolyLine2 } from "./polyLine2Types";
import { polyLine2New } from "./polyLine2Types";
import type { IndexAABB2, IndexAABB3 } from "./rtree";
import { rtreeBRepFaces, rtreeFaceLoops } from "./rtree";
import type { Vec2 } from "./vec2";
import { vec2Dist, vec2Dot, vec2Len, vec2Lerp, vec2Scale, vec2Sub } from "./vec2";
import { vec3Dist } from "./vec3";

const { sqrt } = Math;

export function closestParamNormalizedLine2(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  x: number,
  y: number,
  clamp?: boolean,
): number {
  const [dx, dy] = vec2Sub(x1, y1, x0, y0);
  const l = sqrt(dx * dx + dy * dy);

  const t = closestParamLine2(x0, y0, x1, y1, x, y, clamp);
  return t / l;
}

export function closestParamLine2(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  x: number,
  y: number,
  clamp = true,
): number {
  const [dx, dy] = vec2Sub(x1, y1, x0, y0);
  const l = sqrt(dx * dx + dy * dy);

  const [rx, ry] = vec2Scale(dx, dy, 1 / l);
  const [ox, oy] = vec2Sub(x, y, x0, y0);
  const do2ptr = vec2Dot(ox, oy, rx, ry);

  if (clamp && do2ptr < 0) {
    return 0;
  }

  if (clamp && do2ptr > l) {
    return l;
  }

  return do2ptr;
}

export function closestPointLine2(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  x: number,
  y: number,
  clamp = true,
): Vec2 {
  // If the line is zero length, this function tries to return a sensible result - the start pt.
  if (vec2Dist(x0, y0, x1, y1) < DIST_EPSILON) {
    return [x0, y0];
  }

  const t = closestParamLine2(x0, y0, x1, y1, x, y, clamp);
  return line2Eval(x0, y0, x1, y1, t, false);
}

/**
 * Returns the Euclidean distance from point (px, py) to the closest point on
 * segment (ax, ay)-(bx, by), clamped to the segment endpoints.
 */
export function distPointToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const [cx, cy] = closestPointLine2(ax, ay, bx, by, px, py, true);
  return vec2Dist(px, py, cx, cy);
}

export function closestPointPolyLine2Edge(
  pl: PolyLine2,
  edgeId: number,
  x: number,
  y: number,
  clamp: boolean = true,
): Vec2 {
  const edge = polyLine2Edge(pl, edgeId);
  return closestPointLine2(edge[0], edge[1], edge[2], edge[3], x, y, clamp);
}

export function closestPointBRep(
  brep: BRep,
  x: number,
  y: number,
  z: number,
  searchRadius: number = 5,
  rtree?: RBush3D,
) {
  const faces = brep.faces;
  let boxes: IndexAABB3[];

  const searchBox = {
    minX: x - searchRadius,
    minY: y - searchRadius,
    minZ: z - searchRadius,
    maxX: x + searchRadius,
    maxY: y + searchRadius,
    maxZ: z + searchRadius,
  };

  if (!rtree && faces.length < 200) {
    boxes = [];
    for (let i = 0, l = faces.length; i < l; i++) {
      const bb = faceAABB3(faces[i]) as IndexAABB3;
      bb.index = i;

      if (aabb3Overlaps(bb, searchBox)) {
        boxes.push(bb);
      }
    }
  } else {
    rtree = rtree || rtreeBRepFaces(brep);
    boxes = rtree.search(searchBox) as IndexAABB3[];
  }

  // there is nothing within the search radius
  if (boxes.length === 0) {
    return null;
  }

  let minx;
  let miny;
  let minz;
  let minu;
  let minv;
  let minFaceIndex;
  let minLoopIndex;
  let minEdgeIndex;
  let mindist = Number.MAX_VALUE;

  for (const box of boxes) {
    // compute distance to edge
    const faceIndex = box.index;
    const face = faces[faceIndex];

    const cpt = closestPointFace(face, x, y, z);
    const [lx, ly, lz] = planeEval(face.plane, cpt.x, cpt.y);

    const d = vec3Dist(x, y, z, lx, ly, lz);

    if (d < mindist) {
      mindist = d;
      minFaceIndex = faceIndex;
      minLoopIndex = cpt.loopIndex;
      minEdgeIndex = cpt.edgeIndex;
      minx = lx;
      miny = ly;
      minz = lz;
      minu = cpt.x;
      minv = cpt.y;
    }
  }

  return {
    x: minx,
    y: miny,
    z: minz,
    u: minu,
    v: minv,
    faceIndex: minFaceIndex,
    loopIndex: minLoopIndex,
    edgeIndex: minEdgeIndex,
  };
}

export interface ClosestPointToPolyLine2 {
  readonly edgeIndex: number;
  readonly t: number;
  readonly x: number;
  readonly y: number;
  readonly dist: number;
}

export function closestPointPolyLine2(pl: PolyLine2, x: number, y: number, clamp = true): ClosestPointToPolyLine2 {
  let mindist = Number.MAX_VALUE;
  let minx = -0;
  let miny = -0;
  let mint = -0;
  let minedge = -1;

  for (let i = 0, l = pl.length - 2; i < l; i += 2) {
    const ax = pl[i];
    const ay = pl[i + 1];

    const bx = pl[i + 2];
    const by = pl[i + 3];

    const t = closestParamNormalizedLine2(ax, ay, bx, by, x, y, clamp);
    const [lx, ly] = vec2Lerp(ax, ay, bx, by, t);

    const d = vec2Dist(x, y, lx, ly);

    if (d < mindist) {
      mindist = d;
      minedge = i / 2;
      minx = lx;
      miny = ly;
      mint = t;
    }
  }

  return {
    edgeIndex: minedge,
    t: mint,
    x: minx,
    y: miny,
    dist: mindist,
  };
}

export function closestPointFace(face: Face, x: number, y: number, z: number, maxDist: number = 5) {
  // closest pt to plane is normal to srf
  const [u, v] = planeProject(face.plane, x, y, z);
  return closestPointFaceFromUV(face, u, v, maxDist);
}

export function closestPointFaceFromUV(face: Face, x: number, y: number, maxDist: number = 5) {
  const loops = face.loops;
  const bb = polyLine2AABB2(loops[0]);

  // if outside the bb, the closest point is on the outer loop
  if (!aabb2ContainsVec2(bb, x, y)) {
    const cp = closestPointPolyLine2(loops[0], x, y);
    return {
      loopIndex: 0,
      ...cp,
    };
  }

  const searchBox = {
    minX: x - maxDist,
    minY: y - maxDist,
    maxX: x + maxDist,
    maxY: y + maxDist,
  };

  let boxes: IndexAABB2[];

  if (loops.length < 100) {
    (bb as IndexAABB2).index = 0;
    boxes = [bb as IndexAABB2];

    for (let i = 1, l = loops.length; i < l; i++) {
      const aabb = polyLine2AABB2(loops[i]) as IndexAABB2;
      aabb.index = i;

      if (aabb2Overlaps(aabb, searchBox)) {
        boxes.push(aabb);
      }
    }
  } else {
    boxes = rtreeFaceLoops(face).search(searchBox);
  }

  for (const box of boxes) {
    // outer loop
    if (box.index === 0) {
      if (!classifyContainmentVec2PolyLine2Fast(x, y, loops[0])) {
        const cp = closestPointPolyLine2(loops[0], x, y);
        return {
          loopIndex: 0,
          ...cp,
        };
      }

      continue;
    } else if (classifyContainmentVec2PolyLine2Fast(x, y, loops[box.index])) {
      const cp = closestPointPolyLine2(loops[0], x, y);
      return {
        loopIndex: box.index,
        ...cp,
      };
    }
  }

  // not inside inner loop and not outside outer loop, so the point is on the face!
  return {
    x,
    y,
    loopIndex: -1,
    edgeIndex: -1,
    t: 0,
  };
}

/**
 * Tolerance for matching edges in overlapping edge map functions.
 * If the distance is below this, data from the input edge will be mapped to the output edge.
 */
const REMAP_EDGE_DELTA_THRESHOLD = 0.01;

/**
 * Creates a map of edge indices from the source polyline edges to the target polyline edges.
 * Caters to scenarios where the polylines are completely overlapping (e.g. coincident vertices and adjacent collinear edges removed).
 *
 * This is acknowledgeably a slow method.
 * {@link polyLine2MergeCoincidentVerticesExtended} should be combined with a similar extension of
 * {@link polyLine2MergeCollinearEdges} so that remapping can be done efficiently on the combination of
 * the two operations.
 */
export function polyLine2OverlappingEdgeMapSlow(
  plSource: PolyLine2 | undefined,
  plTarget: PolyLine2 | undefined,
): { [key: number]: number[] } {
  if (!plSource || !plTarget) {
    return [];
  }

  // If the polylines do not have at least one edge, then there can be no overlap.
  if (plSource.length < 4 || plTarget.length < 4) {
    return [];
  }

  const sourceHasMostEdges = plSource.length >= plTarget.length;
  const mostEdges = sourceHasMostEdges ? plSource : plTarget;
  const leastEdges = sourceHasMostEdges ? plTarget : plSource;

  const edgeMapping: (number | undefined)[] = [];
  const edgeCount = polyLine2EdgeCount(mostEdges);
  for (let i = 0; i < edgeCount; i++) {
    const [cx, cy] = polyLine2EdgeEval(mostEdges, i, 0.5, true);
    const result = closestPointPolyLine2(leastEdges, cx, cy, true);

    if (!result || result.dist > REMAP_EDGE_DELTA_THRESHOLD) {
      // Doesn't overlap so there's no mapping for this edge.
      edgeMapping[i] = undefined;
      continue;
    }
    if (result.edgeIndex === -1) {
      edgeMapping[i] = undefined;
      continue;
    }
    edgeMapping[i] = result.edgeIndex;
  }

  if (sourceHasMostEdges) {
    const edgeMappingAsTable: { [key: number]: number[] } = {};
    for (const [edgeIndex, targetEdgeIndex] of edgeMapping.entries()) {
      if (targetEdgeIndex === undefined) {
        continue;
      }
      if (!edgeMappingAsTable[edgeIndex]) {
        edgeMappingAsTable[edgeIndex] = [targetEdgeIndex];
      } else {
        edgeMappingAsTable[edgeIndex].push(targetEdgeIndex);
      }
    }

    for (let i = 0; i < edgeCount; i++) {
      const result = edgeMappingAsTable[i];
      if (!result) {
        edgeMappingAsTable[i] = [];
      }
    }

    return edgeMappingAsTable;
  }

  const edgeMappingAsTable: { [key: number]: number[] } = {};
  for (const [edgeIndex, targetEdgeIndex] of Object.entries(edgeMapping)) {
    if (targetEdgeIndex === undefined) {
      continue;
    }
    if (!edgeMappingAsTable[targetEdgeIndex]) {
      edgeMappingAsTable[targetEdgeIndex] = [+edgeIndex];
    } else {
      edgeMappingAsTable[targetEdgeIndex].push(+edgeIndex);
    }
  }

  for (let i = 0; i < edgeCount; i++) {
    const result = edgeMappingAsTable[i];
    if (!result) {
      edgeMappingAsTable[i] = [];
    }
  }

  return edgeMappingAsTable;
}

/**
 * This function relies on the two outer arrays being the same length and the polylines within being overlapping.
 */
export function polyLine2ArrayEdgeMapSlow(pls: PolyLine2[], plsTarget: PolyLine2[]): { [index: number]: number[] }[] {
  return pls.map((pl, i) => polyLine2OverlappingEdgeMapSlow(pl, plsTarget[i]));
}

const WELD_TOLERANCE = 0.125;

/**
 * weld edges will align edgeIndexB from polyline B with edgeIndexA from polyline A.
 * @returns polyline B with updated vertex positions
 */
export function polyLine2WeldEdges(
  a: PolyLine2,
  edgeIndexA: number,
  b: PolyLine2,
  edgeIndexB: number,
  tol = WELD_TOLERANCE,
): boolean {
  const edgeA = polyLine2Edge(a, edgeIndexA);

  const [sptX, sptY] = polyLine2EdgeStart(b, edgeIndexB);
  const [sCptX, sCptY] = closestPointLine2(edgeA[0], edgeA[1], edgeA[2], edgeA[3], sptX, sptY, false);

  const [sdx, sdy] = vec2Sub(sCptX, sCptY, sptX, sptY);
  const sd = vec2Len(sdx, sdy);

  const [eptX, eptY] = polyLine2EdgeEnd(b, edgeIndexB);
  const [eCptX, eCptY] = closestPointLine2(edgeA[0], edgeA[1], edgeA[2], edgeA[3], eptX, eptY, false);

  const [edx, edy] = vec2Sub(eCptX, eCptY, eptX, eptY);
  const ed = vec2Len(edx, edy);

  let welded = false;

  // we are looking for vertices that are not already
  // welded and fall below the tolerance

  if (sd > 0 && sd < tol) {
    polyLine2MoveVertices(b, [edgeIndexB], sdx, sdy);
    welded = true;
  }

  if (ed > 0 && ed < tol) {
    polyLine2MoveVertices(b, [edgeIndexB + 1], edx, edy);
    welded = true;
  }

  return welded;
}

export function polyLine2InsertVertexAtPoint(pl: PolyLine2, desiredX: number, desiredY: number): PolyLine2 {
  const { edgeIndex, x, y } = closestPointPolyLine2(pl, desiredX, desiredY);

  const newOutlineVerts: number[] = [];

  newOutlineVerts.push(...pl.slice(0, 2 * edgeIndex + 2));
  newOutlineVerts.push(x, y);
  newOutlineVerts.push(...pl.slice(2 * edgeIndex + 2));

  return polyLine2MergeCoincidentVertices(polyLine2New(newOutlineVerts));
}
